import React, { useContext, useState } from 'react';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { cellValueHighLight } from '@src/utils/tableUtils';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Table } from '@src/common/Table';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import CryptoJS from 'crypto-js';
import { encdata } from '@src/constants';

import { Box } from '@uhg-abyss/web/ui/Box';
import { LoadingScreen } from '@src/common/LoadingScreen';
import {
  useLazyGetReportsQuery,
  useLazySetContinueProcessingQuery,
  useLazyGetRejectReprocessingQuery,
  useLazyGetOverrideReprocessingQuery,
  useLazyGetNoOverrideReprocessingQuery,
} from '@src/store/services/cdb/onlineServices';
import {
  useLazyGetGPSReportsQuery,
  useLazySetGPSContinueProcessingQuery,
} from '@src/store/services/gps/onlineServices';
import { Card } from '@uhg-abyss/web/ui/Card';
import { FileContext } from '@src/routes/Routes';

import { useAuth } from '@src/Providers/AuthProvider';
import { maskUsingUserAccess } from '@src/utils';

export const AllTransactions = ({ setId, procStatus, fileId }) => {
  const router = useRouter();
  const { service } = router.getRouteParams();
  const { fileData } = useContext(FileContext);
  const { user } = useAuth();
  const [continueTrigger] = useLazySetContinueProcessingQuery();
  const [rejectReprocessing] = useLazyGetRejectReprocessingQuery();
  const [overrideReprocessing] = useLazyGetOverrideReprocessingQuery();
  const [noOverrideReprocessing] = useLazyGetNoOverrideReprocessingQuery();
  const { toast } = useToast();

  const [isExtType, setIsExtType] = useState(
    user?.userType === 'E' ? true : false
  );
  const columns = React.useMemo(() => {
    return [
      {
        Header: 'Severity',
        accessor: 'severityCode',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 80,
        width: 80,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: service === 'gps' ? 'Medicare ID' : 'Employee ID',
        accessor: 'subscrId',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        minWidth: 120,
        width: 120,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Trans Id',
        accessor: 'transId',
        canToggleVisibilty: false,
        isHiddenByDefault: true,
        minWidth: 100,
        width: 100,
      },

      {
        Header: 'Medicare ID',
        accessor: service === 'gps' ? 'medicareId' : 'medId',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Family ID',
        accessor: 'familyId',
        canToggleVisibilty: false,
        isHiddenByDefault: service === 'gps' ? false : true,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Name',
        accessor: 'name',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 120,
        width: 120,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Rel',
        accessor: service === 'gps' ? 'mbrTicRelCd' : 'relationship',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 50,
        width: 50,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Coverage Type',
        accessor: 'covType',
        minWidth: 80,
        width: 80,
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Status',
        accessor: service === 'gps' ? 'status' : 'stsTxt',
        minWidth: 90,
        width: 90,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Type',
        accessor: 'type',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 130,
        width: 130,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Transaction Source',
        accessor: 'transactionSource',
        minWidth: 85,
        width: 85,
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Warning Name',
        accessor: 'warningName',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Data field',
        accessor: 'dataField',
        minWidth: 100,
        width: 100,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Customer Data',
        accessor: service === 'gps' ? 'customerData' : 'custData',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
          });
        },
      },
      {
        Header: 'Source Data',
        accessor: 'sourceData',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
          });
        },
      },

      {
        Header: 'Detail View',
        accessor: 'detailView',
        enableSorting: false,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row }) => {
          const nameSplit = row?.original?.name?.split(',') || [];
          const firstName = nameSplit[1]?.trim() || '';
          const lastName = nameSplit[0]?.trim() || '';
          const typeValue = row?.original?.multiTransId?.trim();
          const value =
            service !== 'gps' &&
            fileData?.multilevelSubmitter &&
            typeValue?.length > 0
              ? 'View'
              : 'Detail View';

          const link =
            service === 'gps'
              ? `/home/gps-detail-view/`
              : fileData?.multilevelSubmitter && typeValue?.length > 0
              ? `/home/multi-level-view/`
              : `/home/detail-view/`;

          const linkData = {
            fileID: fileData?.fileID || fileData.fileId,
            transSetId: fileData?.transSetId,
            medicareId: row?.original?.medicareId,
            firstName: firstName,
            lastName: lastName.trim(),
            processingDt: fileData?.processingDt,
            customerName: fileData?.customerName,
            mbrTicRelCd: row?.original?.mbrTicRelCd,
            transId: row?.original?.transId,
            subscrId: row?.original?.subscrId,
            sbmtGrpName: fileData?.sbmtGrpName,
            relationship: row?.original?.relationship,
            cescustnbr: fileData?.cescustnbr,
            sbmtGrpId: fileData?.sbmtGrpId,
            reportType: 'AT',
            // Add other parameters as needed
          };

          const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(linkData),
            encdata
          ).toString();

          const newUrl = `${link}${encodeURIComponent(encryptedData)}`;

          return cellValueHighLight({
            value: (
              <Flex
                css={{
                  gap: '10px',
                }}
              >
                <Link
                  size="$sm"
                  target="_blank"
                  href={newUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(newUrl, 'DetailViewTab');
                  }}
                >
                  <Button
                    variant="outline"
                    size="$sm"
                    css={{
                      fontSize: '12px',
                    }}
                  >
                    <strong>{value}</strong>
                  </Button>
                </Link>
              </Flex>
            ),
            fileData,
            typeValue,
          });
        },
      },
    ];
  }, [user]);

  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [actionType, setActionType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // Use fileData.fileId as fallback if fileId prop is not provided
  const actualFileId = fileId || fileData?.fileId || fileData?.fileID;

  const handleReprocessAction = async (reportType) => {
    setIsLoading(true);
    try {
      switch (reportType) {
        case 'reject': {
          const { data, status } = await rejectReprocessing({
            tranSetId: setId,
            tranFileId: actualFileId,
          });
          toast.show({
            id: 'reject-successful',
            withCloseButton: true,
            title: '',
            message: data,
            type: 'success',
            ariaLoadingLabel: 'Rejected all transactions',
          });
          break;
        }
        case 'continueProcessing': {
          const { data, isError } = await continueTrigger({
            tranSetId: setId,
            tranFileId: actualFileId,
          });
          toast.show({
            id: 'continueProcessing-successful',
            withCloseButton: true,
            title: '',
            type: isError ? 'error' : 'success',
            message: isError
              ? 'Failed to Continue Processing Transactions'
              : data,
            ariaLoadingLabel: 'Continue Processing transactions',
          });
          break;
        }
        case 'reprocessOverride': {
          const { data, isError } = await overrideReprocessing({
            tranSetId: setId,
            tranFileId: actualFileId,
          });
          toast.show({
            id: 'reprocessOverride-successful',
            withCloseButton: true,
            title: '',
            type: isError ? 'error' : 'success',
            message: isError
              ? 'Failed to ReprocessOverride transactions'
              : data,
            ariaLoadingLabel: 'ReprocessOverride transactions',
          });
          break;
        }
        case 'reprocessNoOverride': {
          const { data, isError } = await noOverrideReprocessing({
            tranSetId: setId,
            tranFileId: actualFileId,
          });
          toast.show({
            id: 'reprocessNoOverride-successful',
            withCloseButton: true,
            title: '',
            type: isError ? 'error' : 'success',
            message: isError
              ? 'Failed to ReprocessNoOverride transactions'
              : data,
            ariaLoadingLabel: 'ReprocessNoOverride transactions',
          });
          break;
        }
        default: {
          return;
        }
      }
      router.navigate('/');
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (type) => {
    setActionType(type);
    setIsPromptVisible(true);
  };

  const handleConfirm = () => {
    setIsPromptVisible(false);
    handleReprocessAction(actionType);
  };

  const handleCancel = () => {
    setIsPromptVisible(false);
  };

  return (
    <Card
      css={{
        'abyss-card-root': { width: '100%', padding: '0', borderRadius: '4px' },
      }}
    >
      <div
        style={{
          backgroundColor:
            'var(--abyss-colors-web-semantic-color-surface-container-primary)',
          color:
            'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
          fontSize: '18px',
          padding: '24px',
          borderTopLeftRadius: '3px',
          borderTopRightRadius: '3px',
          fontWeight: 'bold',
        }}
      >
        All Transaction
      </div>
      <div style={{ padding: '16px' }}>
        {isLoading && <LoadingScreen />}

        <React.Fragment>
          <Table
            columns={columns}
            dataKey="fileListTable"
            fetch={
              service === 'gps'
                ? useLazyGetGPSReportsQuery
                : useLazyGetReportsQuery
            }
            requestParams={{
              ...router.getRouteParams(),
              ...fileData,
              allDate: 'false',
            }}
            customHeaderSection={
              <React.Fragment>
                {user?.thrshRepr &&
                procStatus?.includes('THRESHOLD_EXCEEDED') ? (
                  <Layout.Group
                    css={{
                      gap: '8px',
                      alignItems: 'flex-end',
                      '.abyss-select-input-root': {
                        minWidth: '288px',
                      },
                    }}
                  >
                    <Button
                      variant="outline"
                      isDisabled={isTableLoading}
                      onClick={() => handleButtonClick('reject')}
                    >
                      <strong>Reject</strong>
                    </Button>
                    <Button
                      variant="outline"
                      isDisabled={isTableLoading}
                      onClick={() => handleButtonClick('continueProcessing')}
                    >
                      <strong>Continue Processing All</strong>
                    </Button>
                    {service !== 'gps' && (
                      <Button
                        variant="outline"
                        isDisabled={isTableLoading}
                        onClick={() => handleButtonClick('reprocessOverride')}
                      >
                        <strong>Reprocess With Override</strong>
                      </Button>
                    )}
                    {service !== 'gps' && (
                      <Button
                        variant="outline"
                        isDisabled={isTableLoading}
                        onClick={() => handleButtonClick('reprocessNoOverride')}
                      >
                        <strong>Reprocess No Override</strong>
                      </Button>
                    )}
                  </Layout.Group>
                ) : null}
              </React.Fragment>
            }
            showGlobalFilter={true}
            onLoadingChange={setIsTableLoading}
          />
        </React.Fragment>
      </div>
      <ModalDialog
        isOpen={isPromptVisible}
        onClose={handleCancel}
        header="Confirmation"
      >
        <div>
          <div className="popup">
            <p>Are you sure you want to proceed with this action?</p>
            <Box
              css={{
                padding: 0,
                background: 'transparent',
                display: 'flex',
                gap: '12px',
                marginTop: '20px',
                alignItems: 'center',
              }}
            >
              <Button onClick={handleConfirm}>Yes</Button>
              <Button
                onClick={handleCancel}
                variant="filled"
                color="destructive"
              >
                No
              </Button>
            </Box>
          </div>
        </div>
      </ModalDialog>
    </Card>
  );
};
